import type { PropsWithChildren, ReactNode } from 'react'
import { Suspense } from 'react'
import { ThemeProvider } from '../components/theme-provider'
import { ToastProvider } from '../components/toast'
import { ErrorBoundary } from './error-boundary'

interface PlatformShellProps extends PropsWithChildren {
  /** Invert app theme relative to its host. */
  reverseTheme?: boolean
  /** Suspense UI chosen by each runtime. Omit to delegate to a parent boundary. */
  suspenseFallback?: ReactNode
  /** Optional persisted theme key. */
  themeStorageKey?: string
  /** Optional controlled palette mode. */
  minimal?: boolean
  /** Optional controlled palette source color. */
  sourceColor?: string
  onMinimalChange?: (minimal: boolean) => void
  onSourceColorChange?: (sourceColor: string) => void
}

/** Shared design-system providers. Compose app-specific providers inside. */
export function PlatformShell({
  children,
  reverseTheme,
  suspenseFallback,
  themeStorageKey,
  minimal,
  sourceColor,
  onMinimalChange,
  onSourceColorChange,
}: PlatformShellProps) {
  const content =
    suspenseFallback === undefined ? (
      children
    ) : (
      <Suspense fallback={suspenseFallback}>{children}</Suspense>
    )

  return (
    <ThemeProvider
      minimal={minimal}
      sourceColor={sourceColor}
      onMinimalChange={onMinimalChange}
      onSourceColorChange={onSourceColorChange}
      storageKey={themeStorageKey}
      reverse={reverseTheme}
    >
      <ToastProvider>
        <ErrorBoundary>{content}</ErrorBoundary>
      </ToastProvider>
    </ThemeProvider>
  )
}
